import { t as ct } from "../lib/content.js";
import { OCR_MAX_WIDTH, checkWords, englishFirst, loadDictionary, loadReader, parseSheet, preprocessImage, readImage } from "../lib/ocr.js";

const { useEffect, useRef, useState } = React;

// Photograph the sheet (LB-363; boards C1–C3, 26 Sep). Three phases in one panel:
//   pick    the phone's own camera or a photo from the roll (one <input type=file>; iOS
//           offers Take Photo / Photo Library itself, so there is no custom camera)
//   frame   the photo with a rectangle whose corners the parent drags, so the heading and the
//           write-then-cover columns never reach the reader (board C1)
//   reading the crop is cleaned (src/lib/ocr.js) and read on the phone; a progress bar with
//           honest stages, the first time longer while the reader loads (board C2)
// then onWords({ pairs, dropped, droppedWhy, lines, flags }) — the caller shows the review (board C3). A
// read that finds nothing, or fails, hands the parent back to typing. The photo never leaves
// the device.
//
// `type` is the list type ("spelling" | "english" | "language"); `known` is a Set of the
// household's own words (lowercase) that the word check must not flag.
//
// `initialFile` (Alan, 27 Sep): the caller's own button has already opened the phone's picker
// (Take Photo / Photo Library / Choose File) and hands the chosen file in, so the panel starts
// at the frame. The "pick" phase stayed only as the landing for a photo that will not open; it
// was a second screen saying "Photograph the sheet" again after the parent had just tapped
// exactly that. The picker has to be opened by the tap itself (iOS refuses a file input
// clicked later), which is why it lives with the caller's button and not in an effect here.
export function PhotoCapture({ type, known, onWords, onCancel, showToast, initialFile = null }) {
  const [phase, setPhase] = useState(initialFile ? "opening" : "pick");
  const [img, setImg] = useState(null); // { el, w, h } natural size
  const [fit, setFit] = useState({ w: 0, h: 0, scale: 1 });
  const [rect, setRect] = useState(null); // display coords {x0,y0,x1,y1}
  const [progress, setProgress] = useState({ stage: "loading", value: 0 });
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const frameRef = useRef(null);
  const frameTopRef = useRef(null); // the "Drag the corners" line, which the frame step anchors on
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);
  useEffect(() => {
    if (initialFile) loadFile(initialFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draw the photo to fit the frame's width once it is loaded or the frame resizes.
  useEffect(() => {
    if (phase !== "frame" || !img || !frameRef.current || !canvasRef.current) return;
    const maxW = frameRef.current.clientWidth || 358;
    // LB-363 follow-up (Alan, 27 Sep): 460px of photo pushed "Read the words" off the bottom of
    // his phone. The frame takes at most half the viewport, so the buttons under it are always
    // on screen, and the step scrolls itself into view below.
    const vh = (typeof window !== "undefined" && window.visualViewport && window.visualViewport.height) || (typeof window !== "undefined" && window.innerHeight) || 800;
    const maxH = Math.max(220, Math.min(460, Math.round(vh * 0.5)));
    const scale = Math.min(maxW / img.w, maxH / img.h, 1);
    const w = Math.round(img.w * scale), h = Math.round(img.h * scale);
    const c = canvasRef.current; c.width = w; c.height = h;
    c.getContext("2d").drawImage(img.el, 0, 0, w, h);
    setFit({ w, h, scale });
    setRect((r) => r || { x0: Math.round(w * 0.06), y0: Math.round(h * 0.06), x1: Math.round(w * 0.94), y1: Math.round(h * 0.94) });
    // Alan, 28 Sep: anchor on the "Drag the corners" line, so the instruction, the photo and the
    // Retake / Read row are on screen together (the photo is at most half the viewport, above).
    // Anchoring on the buttons, as before, scrolled the instruction off the top.
    const top = frameTopRef.current;
    if (top && top.scrollIntoView) {
      try { top.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { top.scrollIntoView(true); }
    }
  }, [phase, img]);

  // LB-363 follow-up (Alan, 27 Sep): the Kid Mode button floats bottom-right and covers "Read
  // the words". It is rendered from App.jsx, far above this component, so the photo step says
  // when it is open and App stands the button down — the same thing it already does for the
  // keyboard, rather than a second mechanism.
  useEffect(() => {
    const open = phase === "frame" || phase === "reading";
    if (typeof window === "undefined") return undefined;
    window.dispatchEvent(new CustomEvent(open ? "lb:photo-open" : "lb:photo-closed"));
    return () => { window.dispatchEvent(new CustomEvent("lb:photo-closed")); };
  }, [phase]);

  function onFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    loadFile(file);
  }
  function loadFile(file) {
    setError(null);
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    let url;
    try { url = URL.createObjectURL(file); } catch {
      setError(ct("photo.error_open", "That photo could not be opened. Try another, or type the words."));
      setPhase("pick");
      return;
    }
    urlRef.current = url;
    const el = new Image();
    el.onload = () => { setImg({ el, w: el.naturalWidth, h: el.naturalHeight }); setRect(null); setPhase("frame"); };
    el.onerror = () => { setError(ct("photo.error_open", "That photo could not be opened. Try another, or type the words.")); setPhase("pick"); };
    el.src = url;
  }

  // Corner drag: pointer capture on the frame, the nearest corner moves, the rectangle keeps
  // at least 40px a side so a stray tap cannot collapse it.
  function corner(x, y) {
    const c = [["x0", "y0"], ["x1", "y0"], ["x0", "y1"], ["x1", "y1"]];
    let best = null; let bestD = Infinity;
    for (const [kx, ky] of c) { const d = Math.hypot(rect[kx] - x, rect[ky] - y); if (d < bestD) { bestD = d; best = [kx, ky]; } }
    return bestD < 40 ? best : null;
  }
  function pos(e) { const r = frameRef.current.getBoundingClientRect(); return [Math.min(fit.w, Math.max(0, e.clientX - r.left)), Math.min(fit.h, Math.max(0, e.clientY - r.top))]; }
  function onDown(e) {
    if (!rect) return;
    const [x, y] = pos(e); const c = corner(x, y);
    if (!c) return;
    dragRef.current = c; e.currentTarget.setPointerCapture(e.pointerId); e.preventDefault();
  }
  function onMove(e) {
    if (!dragRef.current) return;
    const [x, y] = pos(e); const [kx, ky] = dragRef.current;
    setRect((r) => {
      const n = { ...r, [kx]: x, [ky]: y };
      if (n.x1 - n.x0 < 40) { if (kx === "x0") n.x0 = n.x1 - 40; else n.x1 = n.x0 + 40; }
      if (n.y1 - n.y0 < 40) { if (ky === "y0") n.y0 = n.y1 - 40; else n.y1 = n.y0 + 40; }
      return n;
    });
  }
  function onUp() { dragRef.current = null; }

  async function read() {
    if (!img || !rect) return;
    setPhase("reading"); setError(null); setProgress({ stage: "loading", value: 0 });
    try {
      // The crop, from the full photo, at a width the reader likes.
      const sx = rect.x0 / fit.scale, sy = rect.y0 / fit.scale, sw = (rect.x1 - rect.x0) / fit.scale, sh = (rect.y1 - rect.y0) / fit.scale;
      const outScale = Math.min(1, OCR_MAX_WIDTH / sw);
      const w = Math.max(1, Math.round(sw * outScale)), h = Math.max(1, Math.round(sh * outScale));
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      const g = c.getContext("2d");
      g.drawImage(img.el, sx, sy, sw, sh, 0, 0, w, h);
      setProgress({ stage: "cleaning", value: 0.02 });
      const cleaned = preprocessImage(g.getImageData(0, 0, w, h));
      g.putImageData(new ImageData(cleaned.data, w, h), 0, 0);
      const [read, dict] = await Promise.all([
        readImage(c, (stage, value) => setProgress({ stage, value })),
        loadDictionary().catch(() => null),
      ]);
      setProgress({ stage: "checking", value: 0.98 });
      // LB-363 real-sheet round: the clean-up hands back where the table's rules were, and a
      // ruled sheet is read off that geometry rather than off Tesseract's text lines.
      const parsed = parseSheet({ lines: read.lines, words: read.words, rules: cleaned.rules, width: w, height: h }, type);
      if (!parsed.pairs.length) {
        setError(ct("photo.error_nothing", "No words were found in that part of the photo. Try framing it again, or type the words."));
        setPhase("frame");
        return;
      }
      // LB-378: a vocabulary sheet prints the language first as often as second, so which column
      // is English is settled HERE, against the dictionary, before anything is checked against it.
      // Everything below reads column A as the English one, and now it always is.
      const ordered = type === "language" ? englishFirst(parsed.pairs, dict) : { pairs: parsed.pairs, swapped: false };
      const english = ordered.pairs.map((p) => p.a);
      const checked = dict ? checkWords(english, dict, known || new Set()) : english.map((wd) => ({ word: wd, ok: true, suggestion: null }));
      // A word the sheet repeats is kept and put in front of the parent rather than dropped.
      const flags = checked.map((f, i) => (ordered.pairs[i] && ordered.pairs[i].repeat && f.ok ? { ...f, ok: false, why: "repeat" } : f));
      onWords({ pairs: ordered.pairs, dropped: parsed.dropped, droppedWhy: parsed.droppedWhy || [], lines: parsed.lines, flags, swapped: ordered.swapped });
    } catch (e) {
      setError(ct("photo.error_read", "The reader could not run on this phone. Type the words instead."));
      setPhase("frame");
      if (showToast) showToast(ct("photo.error_read_toast", "Reading failed"), "bad");
    }
  }

  if (phase === "opening") {
    return (
      <div className="bg-white rounded-lg px-4 py-3 text-xs font-bold text-slate-500" data-testid="photo-opening">{ct("photo.opening", "Opening the photo")}</div>
    );
  }

  if (phase === "pick") {
    return (
      <div className="bg-white rounded-lg px-4 py-3 flex flex-col gap-2" data-testid="photo-pick">
        <div className="text-xs font-extrabold text-slate-900">{ct("photo.pick_title", "Photograph the sheet")}</div>
        <p className="text-xs text-slate-500">{ct("photo.pick_body", "Printed sheets read best. Lay the sheet flat and get close, so the words fill the frame — a close photo reads far better than a distant one. You check every word before it is saved.")}</p>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="photo-file" />
        <button type="button" onClick={() => fileRef.current && fileRef.current.click()} className="w-full bg-slate-900 text-white text-sm font-bold rounded-lg py-3" data-testid="photo-open">{ct("photo.pick_button", "Take or choose a photo")}</button>
        {error && <p className="text-xs text-rose-600" data-testid="photo-error">{error}</p>}
        <p className="text-[11px] text-slate-400">{ct("photo.privacy", "The photo stays on your phone. Nothing is sent anywhere to be read.")}</p>
        <button type="button" onClick={onCancel} className="text-xs font-bold text-slate-900 underline self-start" data-testid="photo-cancel">{ct("photo.type_instead", "Type them instead")}</button>
      </div>
    );
  }

  if (phase === "frame" || phase === "reading") {
    const reading = phase === "reading";
    return (
      <div className="flex flex-col gap-2" data-testid={reading ? "photo-reading" : "photo-frame"}>
        {!reading && <p ref={frameTopRef} className="text-xs text-slate-500" style={{ scrollMarginTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }} data-testid="photo-frame-body">{ct("photo.frame_body", "Drag the corners so only the words are inside. Leave out the heading and any writing columns. Keep the lines of the table in — they help the reader find the rows.")}</p>}
        <div ref={frameRef} className="relative rounded-lg overflow-hidden bg-slate-700 select-none" style={{ touchAction: "none", minHeight: 120 }} onPointerDown={reading ? undefined : onDown} onPointerMove={reading ? undefined : onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          <canvas ref={canvasRef} className="block max-w-full" />
          {rect && (
            <>
              <div className="absolute inset-0 pointer-events-none" style={{ background: "rgba(0,0,0,0.45)", clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${rect.x0}px ${rect.y0}px, ${rect.x0}px ${rect.y1}px, ${rect.x1}px ${rect.y1}px, ${rect.x1}px ${rect.y0}px, ${rect.x0}px ${rect.y0}px)` }} />
              <div className="absolute pointer-events-none" style={{ left: rect.x0, top: rect.y0, width: rect.x1 - rect.x0, height: rect.y1 - rect.y0, border: "2px solid #FFC629" }} data-testid="photo-rect" />
              {[[rect.x0, rect.y0], [rect.x1, rect.y0], [rect.x0, rect.y1], [rect.x1, rect.y1]].map(([x, y], i) => (
                <span key={i} className="absolute pointer-events-none rounded-sm" style={{ left: x - 9, top: y - 9, width: 18, height: 18, background: "#FFC629", boxShadow: "0 0 0 2px rgba(0,0,0,0.25)" }} />
              ))}
            </>
          )}
          {reading && (
            <div className="absolute inset-0 flex items-end justify-center pb-4" style={{ background: "rgba(27,42,74,0.55)" }}>
              <div className="bg-white rounded-lg px-4 py-3 w-11/12 flex flex-col gap-2" data-testid="photo-progress">
                <div className="h-1.5 rounded-full bg-slate-200"><div className="h-1.5 rounded-full bg-slate-900" style={{ width: `${Math.round(100 * Math.min(1, progress.value))}%` }} /></div>
                <div className="text-xs font-bold text-slate-900">{progress.stage === "loading"
                  ? ct("photo.stage_loading", "Loading the reader (once). It runs on your phone and is kept for next time.")
                  : progress.stage === "cleaning" ? ct("photo.stage_cleaning", "Cleaning the photo (shadows, contrast)")
                  : progress.stage === "checking" ? ct("photo.stage_checking", "Checking each word")
                  : ct("photo.stage_reading", "Reading the words")}</div>
              </div>
            </div>
          )}
        </div>
        {error && <p className="text-xs text-rose-600" data-testid="photo-error">{error}</p>}
        <div className="flex gap-2">
          <button type="button" disabled={reading} onClick={() => fileRef.current && fileRef.current.click()} className="flex-1 bg-slate-200 text-slate-900 text-sm font-bold rounded-lg py-3 disabled:opacity-40" data-testid="photo-retake">{ct("photo.retake", "Retake")}</button>
          <button type="button" disabled={reading || !rect} onClick={read} className="flex-[2] bg-slate-900 text-white text-sm font-bold rounded-lg py-3 disabled:opacity-40" data-testid="photo-read">{ct("photo.read", "Read the words")}</button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
        <button type="button" onClick={onCancel} className="text-xs font-bold text-slate-900 underline self-start">{ct("photo.type_instead", "Type them instead")}</button>
        <p className="text-[11px] text-slate-400">{ct("photo.privacy", "The photo stays on your phone. Nothing is sent anywhere to be read.")}</p>
      </div>
    );
  }
  return null;
}

// Warm the reader in the background once a parent has reached the photo step, so "Read the
// words" is quick even the first time. Safe to call more than once.
export function warmReader() {
  try { loadReader(); loadDictionary(); } catch { /* the read will report it */ }
}
